import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../UI';
import type { PlannedWeekData } from '../../../types/weekOverview';
import { UnscheduledInterventionItem } from './UnscheduledInterventionItem';
import styles from './UnscheduledInterventionsModal.module.css';

const MODAL_WIDTH_PX = 560;
// The list grows with the number of unscheduled interventions, so the dialog
// is capped and its content region scrolls instead of outgrowing the viewport.
const MODAL_MAX_HEIGHT = '90vh';

interface UnscheduledInterventionsModalProps {
  weekData: PlannedWeekData;
  onClose: () => void;
}

// Dialog listing the interventions the latest calculation could not place in
// the week's calendar, so the planner can resolve them (free up rooms/staff or
// move other bookings) instead of hunting for what is missing.
export const UnscheduledInterventionsModal = ({
  weekData,
  onClose
}: UnscheduledInterventionsModalProps) => {
  const { t } = useTranslation();

  const patientNameById = useMemo(() => {
    return new Map(weekData.patients.map((patient) => [patient.id, patient.name]));
  }, [weekData.patients]);

  return (
    <Modal
      title={t('week-overview-unscheduled-title')}
      onClose={onClose}
      width={MODAL_WIDTH_PX}
      style={{ maxHeight: MODAL_MAX_HEIGHT, display: 'flex', flexDirection: 'column' }}
      headerStyle={{ flexShrink: 0 }}
      contentStyle={{ padding: 'var(--spacing-md)', overflowY: 'auto', minHeight: 0 }}
    >
      <p className={styles.description}>{t('week-overview-unscheduled-description')}</p>
      <ul className={styles.list}>
        {weekData.unscheduledInterventions.map((intervention) => (
          <UnscheduledInterventionItem
            key={intervention.id}
            intervention={intervention}
            patientNameById={patientNameById}
          />
        ))}
      </ul>
    </Modal>
  );
};
